/* eslint-disable guard-for-in */
/*
 * @file filters.js 用于存放全局过滤器
 * @author liushengxgi
 * @date 2020-03-05 17:23:07
 * ----
 * @last Modified by liushengxgi
 * @last Modified time 2020-03-05 17:23:13
 */
import { currencySymbolFormat, getCurrencyDataFormat, getCurrencyFinanceFormat } from './utils';
/**
 * 数字转换
 * @param {string | number} value
 * @param {number} decimals 小数位数
 */
const numberFormat = (value, decimals = 2) => {
    if (isNaN(value)) {
        return value;
    }
    // 如果返回的数是 int类型的最小值，返回 -
    if (value + '' === '-2147483648') {
        return '-';
    }
    // 将value转成字符串，然后才能使用字符串方法
    value = '' + value;
    let sign = '';
    if (['-', '+'].includes(value[0])) {
        sign = value[0];
        value = value.slice(1);
    }

    let intPart = Number(value) - (Number(value) % 1); // 获取整数部分
    let intPartFormat = intPart.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,'); // 将整数部分逢三一断

    let value2Array = value.toString().split('.');

    // 表示数据有小数位
    if (value2Array.length === 2) {
        let floatPart = value2Array[1].toString();
        floatPart = Number('0.' + floatPart).toFixed(decimals).slice(2);
        return `${sign}${intPartFormat}.${floatPart}`;
    } else {
        return `${sign}${intPartFormat}`;
    }
};

/**
 * 12小时制转换为24小时制
 * @param {string} str - 输入的时间字符: Tuesday, 12 am until 1 am, 4 am until 6 pm
 * @returns {string} 转换后的24小时制字符串: Tuesday, 00:00 - 01:00, 04:00 - 18:00
 */
function convert12to24HourFormat(str) {
    if (!str || typeof str !== 'string') {
        return str;
    }

    // 时间转换函数
    function convertMainFunction(timeStr, isAfterUntil = false) {
        const match = timeStr.match(/(\d{1,2})\s*(am|pm)/i);
        if (!match) return timeStr;

        let hour = parseInt(match[1]);
        const period = match[2].toLowerCase();

        // 特殊处理12am的情况
        if (hour === 12 && period === 'am') {
            // until前面的12am表示00:00，until后面的12am表示24:00
            return isAfterUntil ? '24:00' : '00:00';
        }

        // 处理am时间
        if (period === 'am') {
            if (hour === 12) {
                hour = 0;
            }
        }
        // 处理pm时间
        else if (period === 'pm') {
            if (hour !== 12) {
                hour += 12;
            }
        }

        // 格式化为HH:MM
        return hour.toString().padStart(2, '0') + ':00';
    };

    try {
        // 提取开头的星期几
        const WEEKDAY_PATTERN = /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Mon|Tues|Wed|Thur|Fri|Sat|Sun)/i;
        const weekdayMatch = str.match(WEEKDAY_PATTERN);
        const weekday = weekdayMatch ? weekdayMatch[0] : '';

        // 移除开头的星期几和逗号，获取时间部分
        let timeStr = str.replace(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Mon|Tues|Wed|Thur|Fri|Sat|Sun),?\s*/i, '');

        // 处理时间段
        let result = timeStr;

        // 匹配 "时间 until 时间" 的模式
        result = result.replace(/(\d{1,2}\s*(?:am|pm))\s+until\s+(\d{1,2}\s*(?:am|pm))/gi, (match, startTime, endTime) => {
            const start = convertMainFunction(startTime, false);
            const end = convertMainFunction(endTime, true);
            return `${start} - ${end}`;
        });

        // 处理单独的时间（没有until的）
        result = result.replace(/(\d{1,2}\s*(?:am|pm))/gi, (match) => {
            return convertMainFunction(match, false);
        });

        // 清理多余的连接词
        result = result.replace(/\s*,\s*and\s*/g, ', ');
        result = result.replace(/\s*and\s*/g, ', ');

        // 组合结果
        return weekday ? `${weekday}, ${result}` : result;
    } catch (error) {
        return str;
    }
};

const weekFormat = (str, format) => {
    if (format === undefined) {
        throw new Error('');
    }
    const map1 = {
        Mon: 'Monday',
        Tues: 'Tuesday',
        Wed: 'Wednesday',
        Thur: 'Thursday',
        Fri: 'Friday',
        Sat: 'Saturday',
        Sun: 'Sunday'
    };
    const map2 = {
        Monday: 'Mon',
        Tuesday: 'Tues',
        Wednesday: 'Wed',
        Thursday: 'Thur',
        Friday: 'Fri',
        Saturday: 'Sat',
        Sunday: 'Sun'
    };
    if (format === 1) {
        for(let key in map1){
            if(map1.hasOwnProperty(key)) {
                const r = new RegExp(key, 'ig');
                str = str.replace(r, map1[key]);
            }
        }
        return str;
    }
    if (format === 2) {
        if (str === 'All of the time') {
            return str;
        }
        for(let key in map2){
            if(map2.hasOwnProperty(key)) {
                const r = new RegExp(key, 'ig');
                str = str.replace(r, map2[key]);
            }
        }
        return convert12to24HourFormat(str);
    }
};
// 财务合计加专用货币符号
const currencyFinanceFormat = (v) => {
    for(let m in v) {
        const currencySym = v[m].data.currency;
        for(let n in v[m].data) {
            v[m].data[n] = getCurrencyFinanceFormat(currencySym, numberFormat(v[m].data[n]));
        }
    }
};

const twoDecimalPlaces = (num) => {
    return Math.round(num * 100) / 100;
};
function formatCurrency (key){
    return (row) => getCurrencyDataFormat(row.currency, row[key]);
}
function formatSummary(summary, {currency}, decimals = 2) {
    return getCurrencyDataFormat(currency, '') + numberFormat((summary && +summary) || 0, decimals);
}
export default {
    numberFormat,
    weekFormat,
    currencySymbolFormat,
    getCurrencyDataFormat,
    getCurrencyFinanceFormat,
    currencyFinanceFormat,
    twoDecimalPlaces,
    formatCurrency,
    formatSummary
};
